import { Tag as TagIcon, X, Bell, BellOff, Edit, Share, Plus, Download } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import CardHeaderLabel from "./CardHeaderLabel";

interface BlogCardHeaderProps {
  selectedPost: { id: string } | null;
  isAdmin: boolean;
  hasUser: boolean;
  isSubscribed: (subscriptionType: string, targetId?: string) => boolean;
  toggleSubscription: (subscriptionType: string, targetId?: string) => Promise<boolean | void>;
  subscriptionsLoading: boolean;
  onShowAuthModal: () => void;
  onBackToList: () => void;
  onEditPost: () => void;
  onSharePost: () => void;
  onCreatePost: () => void;
  onExportPosts?: () => void;
  availableTags: Array<{ id: string; name: string; slug: string }>;
  selectedTagFilter: string | null;
  onTagFilterChange: (value: string | null) => void;
  sortOrder: "latest" | "oldest";
  onSortOrderChange: (value: "latest" | "oldest") => void;
  onHeaderClick: () => void;
}

const BlogCardHeader = ({
  selectedPost,
  isAdmin,
  hasUser,
  isSubscribed,
  toggleSubscription,
  subscriptionsLoading,
  onShowAuthModal,
  onBackToList,
  onEditPost,
  onSharePost,
  onCreatePost,
  onExportPosts,
  availableTags,
  selectedTagFilter,
  onTagFilterChange,
  sortOrder,
  onSortOrderChange,
  onHeaderClick,
}: BlogCardHeaderProps) => {
  
  const handleToggleNewPostSubscription = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasUser) {
      onShowAuthModal();
      return;
    }
    const wasSubscribed = isSubscribed("new_post");
    await toggleSubscription("new_post");
    toast.success(
      wasSubscribed 
        ? "You've unfollowed new blog posts" 
        : "You're now following new blog posts"
    );
  };

  const handleToggleCommentSubscription = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasUser) {
      onShowAuthModal();
      return;
    }
    if (!selectedPost) return;
    const wasSubscribed = isSubscribed("new_comment", selectedPost.id);
    await toggleSubscription("new_comment", selectedPost.id);
    toast.success(
      wasSubscribed 
        ? "You've unfollowed comments on this post" 
        : "You're now following comments on this post"
    );
  };

  // Header controls for detail view (selected post)
  const detailViewControls = selectedPost && (
    <div className="flex items-center gap-1 sm:gap-2">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onBackToList();
        }}
        className="text-primary hover:text-primary/80 text-sm font-semibold flex items-center gap-1"
      >
        ← <span className="hidden sm:inline">Back to List View</span><span className="sm:hidden">Back</span>
      </button>
      {isAdmin && (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onEditPost();
          }}
          className="gap-1 px-2 sm:px-3"
        >
          <Edit className="w-4 h-4" />
          <span className="hidden sm:inline">Edit</span>
        </Button>
      )}
      <Button
        size="sm"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          onSharePost();
        }}
        className="gap-1 px-2 sm:px-3"
      >
        <Share className="w-4 h-4" />
        <span className="hidden sm:inline">Share</span>
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={handleToggleCommentSubscription}
        disabled={subscriptionsLoading}
        className="gap-1 px-2 sm:px-3"
      >
        {isSubscribed("new_comment", selectedPost.id) ? (
          <>
            <span className="hidden sm:inline">Unfollow</span>
            <BellOff className="w-4 h-4" />
          </>
        ) : (
          <>
            <span className="hidden sm:inline">Follow</span>
            <Bell className="w-4 h-4" />
          </>
        )}
      </Button>
    </div>
  );

  // Header controls for list view (no selected post) - icons only on mobile
  const listViewControls = !selectedPost && (
    <div className="flex items-center gap-1 sm:gap-2">
      <Button
        size="sm"
        variant="ghost"
        onClick={handleToggleNewPostSubscription}
        disabled={subscriptionsLoading}
        className="gap-1 px-2 sm:px-3"
      >
        {isSubscribed("new_post") ? (
          <>
            <span className="hidden sm:inline">Unfollow Posts</span>
            <BellOff className="w-4 h-4" />
          </>
        ) : (
          <>
            <span className="hidden sm:inline">Follow Posts</span>
            <Bell className="w-4 h-4" />
          </>
        )}
      </Button>
      {isAdmin && onExportPosts && (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onExportPosts();
          }}
          className="gap-1 px-2 sm:px-3"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">Export</span>
        </Button>
      )}
      {isAdmin && (
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onCreatePost();
          }}
          className="gap-1 px-2 sm:px-3"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Post</span>
        </Button>
      )}
    </div>
  );

  // Sub-header with filters (only in list view)
  const filterSubHeader = !selectedPost && (
    <div 
      data-no-drag="true"
      className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-t border-border/50 bg-muted/30"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2">
        <TagIcon className="h-3.5 w-3.5 text-muted-foreground" />
        <Select
          value={selectedTagFilter || "all"}
          onValueChange={(value) => onTagFilterChange(value === "all" ? null : value)}
        >
          <SelectTrigger className="w-[120px] sm:w-[140px] h-7 text-xs" aria-label="Filter posts by topic">
            <SelectValue placeholder="All Topics" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            <SelectItem value="all">All Topics</SelectItem>
            {availableTags.map((tag) => (
              <SelectItem key={tag.id} value={tag.slug}>
                {tag.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedTagFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onTagFilterChange(null);
            }}
            className="h-7 px-2 gap-1"
          >
            <X className="w-3 h-3" />
            <span className="hidden sm:inline">Clear</span>
          </Button>
        )}
      </div>

      <select
        value={sortOrder}
        onChange={(e) => {
          e.stopPropagation();
          onSortOrderChange(e.target.value as "latest" | "oldest");
        }}
        onClick={(e) => e.stopPropagation()}
        className="text-xs px-2 py-1 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-focus h-7"
        aria-label="Sort posts by date"
      >
        <option value="latest">Latest First</option>
        <option value="oldest">Oldest First</option>
      </select>
    </div>
  );

  return (
    <div
      data-no-drag="true"
      className="flex flex-col border-b border-border bg-muted/50 transition-colors cursor-pointer hover:bg-muted/70"
      onClick={onHeaderClick}
    >
      <CardHeaderLabel label="Blog" className="text-black">
        <div data-no-drag="true" onClick={(e) => e.stopPropagation()}>
          {selectedPost ? detailViewControls : listViewControls}
        </div>
      </CardHeaderLabel>
      {filterSubHeader}
    </div>
  );
};

export default BlogCardHeader;
